import { useEffect, useMemo, useState } from 'react';
import { connect } from 'redux-bundler-react';

import ReadOnlyMap from '@components/map/readOnlyMap';
import { SummaryBox, SummaryBoxContent, SummaryBoxHeading, Tag } from '@trussworks/react-uswds';
import { RequestTypeStrings } from '@src/utils/enums';
import { calculateGeometryExtent } from '@src/utils/helpers';
import DistrictModal from '@components/map/modals/districtModal';
import { useFormContext } from 'react-hook-form';
import ProjectLocationSummary from '@components/project-location/ProjectLocationSummary';
import JpaOrgSummary from '@pages/Resources/components/JointPermitApplications/JpaOrgSummary';
import { useModal } from '@stores';

import './projectConfirmationTab.scss';
import { useGetDistrictLocationFromGeometry } from '@src/api/services/aor/hooks';

const requestTitles = {
  ...RequestTypeStrings,
  1: 'Aquatic Resource Delineation Report and Aquatic Resource Inventory',
  2: 'Jurisdictional Determination',
};

const ProjectConfirmationTab = connect(
  'doUpdateRequestLocation',
  'selectRequestFormData',
  ({ activeTab, doUpdateRequestLocation, hasJpa, orgData, requestFormData, requestType }) => {
    const isJdPermit = requestType?.includes('2');
    const isPcnPermit = requestType?.includes('4');
    const isViolationComplaint = requestType?.includes('6');

    const { openSecondaryModal } = useModal();
    const [district, setDistrict] = useState(requestFormData?.location?.district);
    const { watch } = useFormContext();

    const country = watch('country');
    const location = requestFormData?.location;
    const geometry = location?.geometry;
    const summaryData = {
      ...location,
      country,
      district,
    };

    const districtsLocations = useGetDistrictLocationFromGeometry(geometry);
    const districtOptions = useMemo(
      () => [...new Set(districtsLocations?.map((d) => d.district) || [])],
      [districtsLocations]
    );

    useEffect(() => {
      setDistrict(requestFormData?.location?.district);
    }, [requestFormData?.location?.district]);

    useEffect(() => {
      if (districtOptions.length > 1 && (!district || !districtOptions.includes(district))) {
        openSecondaryModal(DistrictModal, { setDistrict });
      } else if (districtOptions.length === 1 && districtOptions[0] !== district) {
        setDistrict(districtOptions[0]);
      }
    }, [districtOptions, district, openSecondaryModal]);

    useEffect(() => {
      doUpdateRequestLocation({ district: district });
    }, [doUpdateRequestLocation, district]);

    return (
      <>
        <div className='grid-row grid-gap-2'>
          {activeTab === 2 ? (
            <div className='desktop:grid-col-6 tablet:grid-col margin-bottom-1'>
              <ReadOnlyMap geometry={geometry} extent={calculateGeometryExtent(geometry)} height='100%' />
            </div>
          ) : null}
          <div className='desktop:grid-col-6 tablet:grid-col margin-bottom-1'>
            <ProjectLocationSummary
              areaLabel={isViolationComplaint ? 'Area of Violation:' : 'Project Area'}
              directionsLabel={isViolationComplaint ? 'Directions to Site Location:' : 'Directions to Project Location'}
              summaryData={summaryData}
            />
          </div>
        </div>
        <div className='margin-top-2'>
          <SummaryBox>
            <SummaryBoxContent>
              <SummaryBoxHeading headingLevel='h4'>Please review the following:</SummaryBoxHeading>
              <ul>
                <li>
                  Selected Request{requestType.length > 1 ? 's' : ''}:
                  <div>
                    <Tag>
                      {requestType.length > 1
                        ? 'Aquatic Resource Delineation Report and Jurisdictional Determination'
                        : requestTitles[requestType[0]]}
                    </Tag>
                  </div>
                </li>
                <li className='margin-top-1'>
                  Confirm that the Project Location information above is correct. The project's location determines the
                  range of available requests within that specific geographical area. This information cannot be
                  modified using the RRS once a request has been created.
                </li>
                {isJdPermit && (
                  <>
                    <li>
                      It is agency practice to honor requests for JDs unless it is clearly impracticable to do so, such
                      as when the USACE is unable to gain access to a site to complete a JD or the USACE lacks other
                      information necessary to respond to the request based on a sound technical record.
                    </li>
                    <li>
                      A jurisdictional determination typically requires a delineation of aquatic resources on the
                      specified property. Persons who would like to request a USACE JD may include an aquatic resources
                      delineation with their request to facilitate timely processing by the USACE. There may be a delay
                      if the USACE needs to conduct the delineation, especially if the project site is large or contains
                      many wetlands, other special aquatic sites, and other waters. Please contact your local USACE
                      Regulatory office to determine current processing times.
                    </li>
                  </>
                )}
                {isPcnPermit && (
                  <li>
                    Please be advised that a Preconstruction Notification (PCN) (ENG 6082) verification relies on a
                    delineation of Aquatic Resources within the project boundary to note where impacts may be occurring.
                    Failure to provide this information may add additional time to the review of your request.
                  </li>
                )}
              </ul>
              <JpaOrgSummary hasJpa={hasJpa} orgData={orgData} />
              <p>
                Press the <span className='text-bold'>Continue</span> button below to begin to your request.
              </p>
              <p>
                If changes are required, press the <span className='text-bold'>Previous</span> button to return to the
                Project Location tab.
              </p>
            </SummaryBoxContent>
          </SummaryBox>
        </div>
      </>
    );
  }
);
export default ProjectConfirmationTab;
